// AgentVerse/client/ui/AgentVerseCopilot.js
import { ComponentBase } from "../core/ComponentBase.js";
// AiCoPilot or RealtimeAiCoPilot will be created as a managed component

export class AgentVerseCopilot extends ComponentBase {
    static styles = `
        /* Styles for the toggle button itself */
        .agentverse-copilot-toggle-btn {
            /* Add any specific styling for the button if needed, e.g., margin */
        }

        /* Modal Styles (same as before) */
        .agentverse-copilot-modal .modal-dialog {
            max-width: 500px;
            margin: 1.75rem auto;
        }
        .agentverse-copilot-modal .modal-content {
            height: 70vh;
            display: flex;
            flex-direction: column;
        }
        .agentverse-copilot-modal .modal-body {
            flex-grow: 1;
            overflow-y: hidden;
            padding: 0;
        }
        .agentverse-copilot-modal .modal-header .btn-close {
            padding: 0.5rem;
        }
    `;

    constructor(config) {
        super(config);
        this.toggleButtonElement = null; // NEW: For the button this component renders
        this.modalElement = null;
        this.modalInstance = null;
        this.coPilotComponent = null;
        this.isModalVisible = false;

        this.agentName = this.config.agentName || "AgentVerseAssistant";
        this.coPilotType = this.config.coPilotType || "AdvancedAIWorkspace";
        this.buttonIcon = this.config.buttonIcon || "bi-stars"; // Customizable icon
        this.buttonTooltip = this.config.buttonTooltip || "AgentVerse Assistant";

        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            agentName: "AgentVerseAssistant",
            coPilotType: "AdvancedAIWorkspace",
            title: "AgentVerse Assistant",
            buttonIcon: "bi-stars", // Default icon for the toggle button
            buttonTooltip: "AgentVerse Assistant",
            buttonClasses: "btn btn-outline-primary btn-sm", // Default classes for the button
        };
    }

    async initialize() {
        await super.initialize();
        // Modal structure is created on first show or if render is called before show
        // No global event listener needed here as the button is part of this component
    }

    createModalStructure() {
        if (this.modalElement) return;

        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade agentverse-copilot-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.setAttribute('aria-labelledby', `${this.config.instanceId}-modalLabel`);
        this.modalElement.setAttribute('aria-hidden', 'true');

        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="${this.config.instanceId}-modalLabel" data-i18n="${this.config.title}">${this.t(this.config.title)}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-copilot-body-container">
                        <!-- AiCoPilot component will be rendered here -->
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);

        this.modalElement.addEventListener('hidden.bs.modal', () => {
            this.isModalVisible = false;
            this.emit('copilotClosed');
        });

        // --- FIX IS HERE ---
        this.modalElement.addEventListener('shown.bs.modal', () => {
            this.isModalVisible = true;
            this.emit('copilotOpened');

            // Now that the modal is fully visible and has dimensions,
            // we can safely tell the AiCoPilot to scroll to the bottom.
            if (this.coPilotComponent && typeof this.coPilotComponent.scrollToBottom === 'function') {
                this.coPilotComponent.scrollToBottom(true); // `true` forces the scroll
            }

            // Also, focus the input area for immediate typing.
            const inputArea = this.coPilotComponent?.inputArea;
            if (inputArea) {
                inputArea.focus();
            }
        });
        // --- END FIX ---
    }

    async ensureCoPilotComponent() {
        if (!this.coPilotComponent) {
            if (!this.modalElement) this.createModalStructure();

            const coPilotBodyContainer = this.modalElement.querySelector(`#${this.config.instanceId}-copilot-body-container`);
            if (!coPilotBodyContainer) {
                this.log.error("CoPilot body container not found in modal.");
                return;
            }

            this.coPilotComponent = await this.createManagedComponent(this.coPilotType, coPilotBodyContainer, {
                instanceId: `${this.config.instanceId}-internal-copilot`,
                agentName: this.agentName,
                singleAgentMode: true,
                height: 'container'
            });
        }

    }


    async show() {
        if (!this.modalInstance) this.createModalStructure();
        await this.ensureCoPilotComponent();
        this.modalInstance.show();
    }

    hide() {
        if (this.modalInstance) {
            this.modalInstance.hide();
        }
    }

    toggle() {
        if (this.isModalVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    render() {
        // This component now renders its own toggle button
        if (!this.toggleButtonElement) {
            this.toggleButtonElement = document.createElement('button');
            this.toggleButtonElement.id = `${this.config.instanceId}-toggle-btn`;
            this.toggleButtonElement.className = this.config.buttonClasses; // Use configured classes
            this.toggleButtonElement.title = this.t(this.config.buttonTooltip);
            this.toggleButtonElement.setAttribute('data-i18n-title', this.config.buttonTooltip);
            this.toggleButtonElement.innerHTML = `<i class="bi ${this.config.buttonIcon}"></i>`;
            this.toggleButtonElement.addEventListener('click', this.toggle.bind(this));
        }
        // The modal is created on demand (first show) and appended to document.body.
        // This render method only returns the button that will trigger the modal.
        return this.toggleButtonElement;
    }

    destroy() {
        this.toggleButtonElement?.removeEventListener('click', this.toggle.bind(this));
        this.coPilotComponent?.destroy();
        if (this.modalInstance) {
            this.modalInstance.dispose();
        }
        this.modalElement?.remove();
        super.destroy();
    }
}